// src/components/PdfWhiteboard/ShapeRenderer.js
import React, { useEffect, useRef, useState, useMemo } from 'react';
import {
  Line,
  Rect,
  Circle,
  Shape,
  Group as KonvaGroup,
} from 'react-konva';
import { TOOLS } from '../constants/drawingTools';
import {
  getDisplayMeasurement,
  resolveShapeScaleProps,
  calculateDistance,
} from '../utils/drawingUtils';
import { not } from 'ajv/dist/compile/codegen';
import TextEditor from '../components/TextEditor';
import RectangleShape from './RectangleShape';
import TextLabelShape from './TextLabelShape';
import MeasurementSummaryShape from './MeasurementSummaryShape';
import DrawingText from './DrawingText';
import VertexHandle from './VertexHandle';
import ArrowShape from './ArrowShape';
import CotationShape from './CotationShape';
import SegmentMeasurementOverlay from './SegmentMeasurementOverlay';
import PolylineShape from './PolylineShape';

import AreaShape from './AreaShape';

import { resolveAllParams } from '../../ShapeList/paramUtils';

const SELECTED_GLOW = {
  shadowColor: '#3B82F6',
  shadowBlur: 18,
  shadowOpacity: 0.7,
  shadowOffsetX: 0,
  shadowOffsetY: 0,
};

// Built-in display key constants for toggling measurement text on shapes
export const DISPLAY_KEY_SEGMENT_LENGTH = '__segment_length__';
export const DISPLAY_KEY_AREA = '__area__';
export const DISPLAY_KEY_MEASUREMENT = '__measurement__';

// Colors
const HOLE_VERTEX_COLOR = null; // '#EF4444'; // Red

// Resolve theme colors once at module level (avoid getComputedStyle per component mount)
const THEME_SELECTION_COLOR = typeof window !== 'undefined'
  ? (getComputedStyle(document.documentElement).getPropertyValue('--pico-primary').trim() || '#3B82F6')
  : '#3B82F6';
const THEME_HOVER_COLOR = typeof window !== 'undefined'
  ? (getComputedStyle(document.documentElement).getPropertyValue('--pico-primary-focus').trim() || '#60A5FA')
  : '#60A5FA';

const arePointsEqual = (a, b) => {
  if (a === b) return true;
  if (!a || !b) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
};

// Use React.memo for potential performance optimization if rendering many shapes
// Custom comparison function to prevent unnecessary re-renders
const ShapeRenderer = React.memo(
  ({
    shape,
    isSelected,
    selectedTool,
    stageScale,
    onShapeClick,
    onShapeDragEnd,
    onVertexClick, // Add new prop
    onTransformEnd,
    currentScaleProps, // Combined scale properties
    dpi, // The new dynamically calculated DPI from the server
    imageSize, // The original pixel dimensions of the image
    isDraggingMode,
    vertexEditInfo, // Add vertex edit state to track if a vertex is being dragged
    onUpdateShape, // Add new prop for updating shape text
    onStartTextEdit, // Callback to start editing text
    textEditingShapeId, // ID of shape currently being edited
    stageRef, // Add stageRef
    groupingKey = '', // Grouping key for displaying params on shapes
    displayKeys = [], // Array of param keys to display as text on shapes
    displayUnitSystem = null,
    allShapes = [], // All shapes for measurement summary
    onShapeSelect, // Shape selection handler for measurement summary
    onShapeHover, // Optional callback to report hover state
    selectedShapeIds = [], // Array of currently selected shape IDs
    allScales = [], // All available scales for locked scale indicator
    isDrawingPreview = false, // AutoCAD projection helpers flag
    showOverlay = true,
    hoveredSegmentInfo = null,
  }) => {
    const [isHovered, setIsHovered] = useState(false);

    // Reset hover state when selection state changes
    useEffect(() => {
      if (isSelected) {
        setIsHovered(false);
      }
    }, [isSelected]);

    const shapeScaleProps = useMemo(() => {
      return resolveShapeScaleProps(shape, currentScaleProps, allScales);
    }, [shape, currentScaleProps, allScales]);

    // Skip rendering if the shape is not visible
    if (!shape.visible) return null;

    // Determine if text should be shown for this shape
    const hasAnySelection = selectedShapeIds.length > 0;
    const shouldShowText = !hasAnySelection || isSelected;

    const isDimmed = hasAnySelection && !isSelected;

    // Shapes are now pre-transformed to match page DPI on load, so no runtime scaling needed
    const originalDPI = shape.originalDPI || dpi; // Keep for measurement calculations
    const scaledShape = shape; // No scaling - use shape as-is

    const shapeStrokeWidth = shape.strokeWidth || 2;
    const shapeStroke = shape.stroke || '#000000';

    // Calculate stroke width considering stage scale, ensure minimum visible size
    const calculatedStrokeWidth = Math.max(
      0.5,
      shapeStrokeWidth / stageScale,
    );
    // Slightly thicker stroke when selected
    const selectedStrokeHighlightColor =
      HOLE_VERTEX_COLOR || shapeStroke; // Defined for consistency
    const selectionStrokeWidth = Math.max(
      0.8,
      (shapeStrokeWidth + 1.5) / stageScale,
    );
    const vertexMarkerRadius = Math.max(3, 5 / stageScale); // Make handles a bit larger

    // Centralized text font size calculation proportional to stroke width
    const baseTextFontSize = Math.max(10, 5 * shapeStrokeWidth);
    const textFontSize = Math.max(10, Math.min(50, baseTextFontSize / (0.95 * stageScale)));
    // Define hover color for all shapes (same as selection for consistency)
    const hoverStrokeColor = THEME_HOVER_COLOR || shapeStroke;

    // Unified selection/hover text/color helper — replaces repeated ternary pattern
    const activeStrokeColor = isSelected 
      ? (THEME_SELECTION_COLOR || shapeStroke) 
      : isHovered 
        ? (THEME_HOVER_COLOR || shapeStroke) 
        : shapeStroke;

    // Hover handlers for reporting state to parent and setting local hover state
    const handleMouseEnter = (e) => {
      if (typeof onShapeHover === 'function') {
        onShapeHover(scaledShape.id, true);
      }

      if (isSelected || vertexEditInfo) return;
      setIsHovered(true);
    };

    const handleMouseLeave = (e) => {
      if (typeof onShapeHover === 'function') {
        onShapeHover(scaledShape.id, false);
      }

      if (isSelected || vertexEditInfo) return;
      setIsHovered(false);
    };

    const commonProps = {
      id: scaledShape.id, // Use shape.id directly as key is handled in the parent map
      stroke: activeStrokeColor,
      strokeWidth: (isSelected || isHovered)
        ? selectionStrokeWidth
        : calculatedStrokeWidth,

      opacity: isDimmed ? 0.35 : 1,

      // Shadow/Glow effects - explicitly set or disable based on selection
      shadowColor: isSelected ? (THEME_SELECTION_COLOR || SELECTED_GLOW.shadowColor) : null,
      shadowBlur: isSelected ? SELECTED_GLOW.shadowBlur : 0,
      shadowOpacity: isSelected ? SELECTED_GLOW.shadowOpacity : 0,
      shadowOffsetX: isSelected ? SELECTED_GLOW.shadowOffsetX : 0,
      shadowOffsetY: isSelected ? SELECTED_GLOW.shadowOffsetY : 0,
      shadowEnabled: isSelected,
      shadowForStrokeEnabled: true, // Enable shadow for strokes so open lines get the selection glow

      draggable: selectedTool === TOOLS.SELECT && isDraggingMode, // Only draggable in select + dragging mode
      onClick: onShapeClick,
      onTap: onShapeClick, // For touch devices
      onDragEnd: onShapeDragEnd,
      onTransformEnd: onTransformEnd,
      rotation: scaledShape.rotation || 0,
      x: scaledShape.x,
      y: scaledShape.y,
      perfectDrawEnabled: false, // Better performance
      // Prevent accidental drag when using drawing tools
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
    };

    function hexToRgba(hex, alpha) {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }

    const isTextTooSmall = (text, metricLength, factor = 1.1) => {
      if (isSelected) return false;
      const estimatedTextWidth = String(text).length * textFontSize * 0.6;
      return metricLength < estimatedTextWidth * factor;
    };

    switch (scaledShape.type) {
      case TOOLS.PEN:
        return (
          <Line
            {...commonProps}
            points={scaledShape.points}
            tension={0.5}
            lineCap="round"
            lineJoin="round"
            listening={true}
            strokeWidth={calculatedStrokeWidth + 20}
            stroke={hexToRgba(commonProps.stroke, 0.5)}
            x={0}
            y={0}
          />
        );
      case TOOLS.RECTANGLE:
        return (
          <RectangleShape
            shape={scaledShape}
            commonProps={commonProps}
            isSelected={isSelected}
            stageScale={stageScale}
            shapeStroke={shapeStroke}
            onStartTextEdit={onStartTextEdit}
            textEditingShapeId={textEditingShapeId}
            onUpdateShape={onUpdateShape}
          />
        );
      case TOOLS.CIRCLE:
        // Konva Circle's x/y is the center, which matches our shape.x/y
        return (
          <KonvaGroup {...commonProps}>
            <Circle
              radius={scaledShape.radius}
              listening={true}
              stroke={commonProps.stroke}
              strokeWidth={commonProps.strokeWidth}
              {...(isSelected ? SELECTED_GLOW : {})}
            />
          </KonvaGroup>
        );
      case TOOLS.POINT: {
        // Render point with the selected marker shape.
        // Old shapes without pointMarker default to 'disk' (original behaviour).
        const markerType = shape.pointMarker || 'disk';
        const crossLen = Math.max(6, vertexMarkerRadius * 2);
        const lineWidth = Math.max(1, 1 / stageScale);
        const dotRadius = Math.max(1.5, vertexMarkerRadius * 0.75);
        const markerRadius = isSelected ? dotRadius * 1.5 : dotRadius;
        const effectiveCrossLen = isSelected ? crossLen * 1.5 : crossLen;

        const markerColor = activeStrokeColor;
        const markerStrokeProps = {
          stroke: '#FFFFFF',
          strokeWidth: Math.max(0.5, 0.75 / stageScale),
        };

        // Expose computed diameter to param resolver
        const calculatedParamsForPoint = {
          Diameter: shape.radius ? (shape.radius * 2).toFixed(2) : '0',
        };
        const allResolvedParamsForPoint = resolveAllParams(
          shape.params,
          calculatedParamsForPoint,
        );

        /**
         * Returns the inner marker element positioned at (0,0) within the Group.
         * For 'cross' we skip the dot entirely — the crosshair IS the marker.
         */
        const renderMarker = () => {
          const commonMarkerProps = {
            listening: true,
            onClick: onShapeClick,
            onTap: onShapeClick,
            perfectDrawEnabled: false,
          };

          switch (markerType) {
            case 'square':
              return (
                <Rect
                  x={-markerRadius * 2}
                  y={-markerRadius * 2}
                  width={markerRadius * 4}
                  height={markerRadius * 4}
                  fill={markerColor}
                  {...markerStrokeProps}
                  {...commonMarkerProps}
                />
              );
            case 'triangle': {
              const tr = markerRadius * 1.4 * 2;
              return (
                <Shape
                  sceneFunc={(ctx, node) => {
                    ctx.beginPath();
                    ctx.moveTo(0, -tr);
                    ctx.lineTo(tr * 0.866, tr * 0.5);
                    ctx.lineTo(-tr * 0.866, tr * 0.5);
                    ctx.closePath();
                    ctx.fillStrokeShape(node);
                  }}
                  fill={markerColor}
                  {...markerStrokeProps}
                  {...commonMarkerProps}
                />
              );
            }
            case 'diamond': {
              const dr = markerRadius * 1.4 * 2;
              return (
                <Shape
                  sceneFunc={(ctx, node) => {
                    ctx.beginPath();
                    ctx.moveTo(0, -dr);
                    ctx.lineTo(dr, 0);
                    ctx.lineTo(0, dr);
                    ctx.lineTo(-dr, 0);
                    ctx.closePath();
                    ctx.fillStrokeShape(node);
                  }}
                  fill={markerColor}
                  {...markerStrokeProps}
                  {...commonMarkerProps}
                />
              );
            }
            case 'star': {
              const outerR = markerRadius * 1.6 * 2;
              const innerR = outerR * 0.4;
              const pts = 5;
              return (
                <Shape
                  sceneFunc={(ctx, node) => {
                    ctx.beginPath();
                    for (let i = 0; i < pts * 2; i++) {
                      const angle = (i * Math.PI) / pts - Math.PI / 2;
                      const r = i % 2 === 0 ? outerR : innerR;
                      if (i === 0) ctx.moveTo(Math.cos(angle) * r, Math.sin(angle) * r);
                      else ctx.lineTo(Math.cos(angle) * r, Math.sin(angle) * r);
                    }
                    ctx.closePath();
                    ctx.fillStrokeShape(node);
                  }}
                  fill={markerColor}
                  {...markerStrokeProps}
                  {...commonMarkerProps}
                />
              );
            }
            case 'cross':
              // 'cross' has no dot — the crosshair lines are the entire marker
              return null;
            case 'disk':
            default:
              return (
                <Circle
                  x={0}
                  y={0}
                  radius={markerRadius}
                  fill={markerColor}
                  {...markerStrokeProps}
                  {...commonMarkerProps}
                />
              );
          }
        };

        // For 'cross' we skip the crosshair lines too (they ARE the marker, drawn below).
        // For all other types the crosshair lines provide the CAD-style cross reference.
        const showCrosshairs = markerType !== 'cross';

        return (
          <KonvaGroup {...commonProps} listening={true}>
            {/* Center marker shape */}
            {renderMarker()}

            {/* Crosshair — horizontal */}
            {showCrosshairs && (
              <Line
                points={[-effectiveCrossLen, 0, effectiveCrossLen, 0]}
                stroke={markerColor}
                strokeWidth={lineWidth / 2}
                listening={false}
                perfectDrawEnabled={false}
                {...(isSelected ? SELECTED_GLOW : {})}
              />
            )}

            {/* Crosshair — vertical */}
            {showCrosshairs && (
              <Line
                points={[0, -effectiveCrossLen, 0, effectiveCrossLen]}
                stroke={markerColor}
                strokeWidth={lineWidth / 2}
                listening={false}
                perfectDrawEnabled={false}
                {...(isSelected ? SELECTED_GLOW : {})}
              />
            )}

            {/* 'cross' marker: two bold lines (longer, thicker than the reference crosshair) */}
            {markerType === 'cross' && (
              <>
                <Line
                  points={[-effectiveCrossLen, 0, effectiveCrossLen, 0]}
                  stroke={markerColor}
                  strokeWidth={Math.max(2, lineWidth * 2.5)}
                  lineCap="round"
                  listening={true}
                  onClick={onShapeClick}
                  onTap={onShapeClick}
                  perfectDrawEnabled={false}
                  {...(isSelected ? SELECTED_GLOW : {})}
                />
                <Line
                  points={[0, -effectiveCrossLen, 0, effectiveCrossLen]}
                  stroke={markerColor}
                  strokeWidth={Math.max(2, lineWidth * 2.5)}
                  lineCap="round"
                  listening={false}
                  perfectDrawEnabled={false}
                  {...(isSelected ? SELECTED_GLOW : {})}
                />
              </>
            )}

            {/* Display keys labels */}
            {shouldShowText && displayKeys.length > 0 && allResolvedParamsForPoint && displayKeys.map((dk, dkIdx) => {
              const val = allResolvedParamsForPoint[dk];
              if (!val) return null;
              const baseOffset = markerType === 'cross'
                ? effectiveCrossLen * 0.2
                : Math.max(markerRadius * 0.2, effectiveCrossLen * 0.2);
              return (
                <DrawingText
                  key={`point-dk-${scaledShape.id}-${dkIdx}`}
                  x={0}
                  y={0}
                  text={`${String(val)}`}
                  fontSize={textFontSize}
                  fill={markerColor}
                  align="center"
                  verticalAlign="middle"
                  offsetX={0}
                  offsetY={
                    -(baseOffset + textFontSize * (0.2 + dkIdx))
                  }
                  stageScale={stageScale}
                  useBadge={true}
                  badgeStroke={markerColor}
                />
              );
            })}
          </KonvaGroup>
        );
      }


      case TOOLS.LINE:
        // Points are relative [0, 0, relX, relY] to shape.x, shape.y
        return (
          <Line
            {...commonProps}
            points={scaledShape.points}
            lineCap="round"
            listening={true}
          />
        );
      case TOOLS.ARROW:
        return (
          <ArrowShape
            shape={scaledShape}
            commonProps={commonProps}
            isSelected={isSelected}
            isHovered={isHovered}
            stageScale={stageScale}
            selectionStrokeWidth={selectionStrokeWidth}
            calculatedStrokeWidth={calculatedStrokeWidth}
            vertexMarkerRadius={vertexMarkerRadius}
            selectedStrokeHighlightColor={selectedStrokeHighlightColor}
            hoverColor={THEME_HOVER_COLOR}
            onVertexClick={onVertexClick}
            shapeStroke={scaledShape.stroke}
            onStartTextEdit={onStartTextEdit}
            textEditingShapeId={textEditingShapeId}
            onUpdateShape={onUpdateShape}
          />
        );
      case TOOLS.COTATION:
        return (
          <CotationShape
            shape={scaledShape}
            commonProps={commonProps}
            isSelected={isSelected}
            stageScale={stageScale}
            vertexMarkerRadius={vertexMarkerRadius}
            selectedStrokeHighlightColor={selectedStrokeHighlightColor}
            hoverColor={THEME_HOVER_COLOR}
            onVertexClick={onVertexClick}
            shouldShowText={shouldShowText}
            displayKeys={displayKeys}
            textFontSize={textFontSize}
            activeStrokeColor={activeStrokeColor}
            shapeScaleProps={shapeScaleProps}
            dpi={dpi}
            imageSize={imageSize}
            displayUnitSystem={displayUnitSystem}
            showOverlay={showOverlay}
          />
        );
      case TOOLS.MEASURE: {
        const [x1, y1, x2, y2] = scaledShape.points;
        const midX = (x1 + x2) / 2;
        const midY = (y1 + y2) / 2;
        const angleRad = Math.atan2(y2 - y1, x2 - x1);
        let angleDeg = angleRad * (180 / Math.PI);

        // Adjust angle for text readability
        if (angleDeg > 90) angleDeg -= 180;
        else if (angleDeg < -90) angleDeg += 180;

        // Use stored measurement data first, fallback to calculation
        // Shapes are transformed to current page DPI, so use current dpi
        const measurement =
          scaledShape.displayMeasurement ||
          getDisplayMeasurement(
            shape.distance || calculateDistance(x1, y1, x2, y2),
            shapeScaleProps,
            dpi,
            imageSize,
            displayUnitSystem
          );

        return (
          <KonvaGroup {...commonProps}>
            <Line
              name="visual_shape"
              points={scaledShape.points}
              stroke={commonProps.stroke}
              strokeWidth={commonProps.strokeWidth}
              lineCap="butt"
              perfectDrawEnabled={false}
              listening={true}
              {...(isSelected ? SELECTED_GLOW : {})}
            />
            {shouldShowText && (displayKeys.includes(DISPLAY_KEY_MEASUREMENT) || displayKeys.includes(DISPLAY_KEY_SEGMENT_LENGTH)) && (
              <DrawingText
                key={`measure-text-${scaledShape.id}`}
                x={midX}
                y={midY}
                text={`${measurement.value} ${measurement.unit}`}
                fontSize={textFontSize}
                fill={activeStrokeColor}
                rotation={angleDeg}
                offsetX={0}
                offsetY={textFontSize * 0.8}
                stageScale={stageScale}
                useBadge={true}
                        badgeStroke={activeStrokeColor}
              />
            )}
            {/* Vertex handles for selected Measure */}
            {isSelected &&
              scaledShape.points &&
              scaledShape.points.map((_, index) => {
                if (index % 2 === 0) {
                  const vertexX = scaledShape.points[index];
                  const vertexY = scaledShape.points[index + 1];
                  return (
                    <VertexHandle
                      key={`vtx-measure-${scaledShape.id}-${index / 2}`}
                      x={vertexX}
                      y={vertexY}
                      radius={vertexMarkerRadius}
                      color={selectedStrokeHighlightColor}
                      hoverColor={THEME_HOVER_COLOR}
                      onMouseDown={(e) =>
                        onVertexClick(e, scaledShape.id, index / 2)
                      }
                      onTouchStart={(e) =>
                        onVertexClick(e, scaledShape.id, index / 2)
                      }
                      stageScale={stageScale}
                    />
                  );
                }
                return null;
              })}
          </KonvaGroup>
        );
      }
      case TOOLS.POLYLINE:
        return (
          <PolylineShape
            shape={scaledShape}
            commonProps={commonProps}
            isSelected={isSelected}
            stageScale={stageScale}
            activeStrokeColor={activeStrokeColor}
            vertexMarkerRadius={vertexMarkerRadius}
            selectedStrokeHighlightColor={selectedStrokeHighlightColor}
            hoverColor={THEME_HOVER_COLOR}
            onVertexClick={onVertexClick}
            shouldShowText={shouldShowText}
            displayKeys={displayKeys}
            textFontSize={textFontSize}
            shapeScaleProps={shapeScaleProps}
            dpi={dpi}
            imageSize={imageSize}
            displayUnitSystem={displayUnitSystem}
            isDrawingPreview={isDrawingPreview}
            vertexEditInfo={vertexEditInfo}
            showOverlay={showOverlay}
            hoveredSegmentInfo={hoveredSegmentInfo}
          />
        );
      case TOOLS.AREA:
        return (
          <AreaShape
            shape={scaledShape}
            commonProps={commonProps}
            isSelected={isSelected}
            stageScale={stageScale}
            activeStrokeColor={activeStrokeColor}
            shapeStroke={shapeStroke}
            calculatedStrokeWidth={calculatedStrokeWidth}
            selectionStrokeWidth={selectionStrokeWidth}
            vertexMarkerRadius={vertexMarkerRadius}
            selectedStrokeHighlightColor={selectedStrokeHighlightColor}
            hoverColor={THEME_HOVER_COLOR}
            onVertexClick={onVertexClick}
            shouldShowText={shouldShowText}
            displayKeys={displayKeys}
            textFontSize={textFontSize}
            shapeScaleProps={shapeScaleProps}
            dpi={dpi}
            imageSize={imageSize}
            displayUnitSystem={displayUnitSystem}
            showOverlay={showOverlay}
            isDrawingPreview={isDrawingPreview}
            vertexEditInfo={vertexEditInfo}
            hoveredSegmentInfo={hoveredSegmentInfo}
          />
        );
      case TOOLS.TEXT:
        return (
          <TextLabelShape
            shape={scaledShape}
            commonProps={commonProps}
            isSelected={isSelected}
            stageScale={stageScale}
            shapeStroke={shapeStroke}
            onStartTextEdit={onStartTextEdit}
            textEditingShapeId={textEditingShapeId}
            onUpdateShape={onUpdateShape}
            standalone={true}
          />
        );

      case TOOLS.MEASUREMENT_SUMMARY:
        return (
          <MeasurementSummaryShape
            shape={shape}
            commonProps={commonProps}
            isSelected={isSelected}
            stageScale={stageScale}
            shapeStroke={shapeStroke}
            onUpdateShape={onUpdateShape}
            allShapes={allShapes}
            currentScaleProps={currentScaleProps}
            dpi={dpi}
            imageSize={imageSize}
            onShapeSelect={onShapeSelect}
            allScales={allScales}
          />
        );

      default:
        console.warn('Unknown shape type:', shape.type);
        return null;
    }
  },
  (prevProps, nextProps) => {
    // Compare scalar props first (fast path — return false ASAP if any differ)
    if (
      prevProps.shape.id !== nextProps.shape.id ||
      prevProps.isSelected !== nextProps.isSelected ||
      prevProps.selectedTool !== nextProps.selectedTool ||
      prevProps.stageScale !== nextProps.stageScale ||
      prevProps.isDraggingMode !== nextProps.isDraggingMode ||
      prevProps.dpi !== nextProps.dpi ||
      prevProps.shape.visible !== nextProps.shape.visible ||
      prevProps.shape.scaleKey !== nextProps.shape.scaleKey ||
      prevProps.currentScaleProps?.key !== nextProps.currentScaleProps?.key ||
      prevProps.currentScaleProps?.ratio !== nextProps.currentScaleProps?.ratio ||
      prevProps.imageSize?.width !== nextProps.imageSize?.width ||
      prevProps.imageSize?.height !== nextProps.imageSize?.height ||
      prevProps.shape.displayMeasurement !== nextProps.shape.displayMeasurement ||
      prevProps.shape.displayAreaMeasurement !== nextProps.shape.displayAreaMeasurement ||
      prevProps.shape.displayTotalMeasurement !== nextProps.shape.displayTotalMeasurement ||
      (prevProps.vertexEditInfo?.shapeId === prevProps.shape.id) !==
      (nextProps.vertexEditInfo?.shapeId === nextProps.shape.id) ||
      prevProps.hoveredSegmentInfo !== nextProps.hoveredSegmentInfo ||
      prevProps.shape.x !== nextProps.shape.x ||
      prevProps.shape.y !== nextProps.shape.y ||
      prevProps.shape.rotation !== nextProps.shape.rotation ||
      prevProps.shape.stroke !== nextProps.shape.stroke ||
      prevProps.shape.strokeWidth !== nextProps.shape.strokeWidth ||
      prevProps.shape.width !== nextProps.shape.width ||
      prevProps.shape.height !== nextProps.shape.height ||
      prevProps.shape.radius !== nextProps.shape.radius ||
      prevProps.shape.text !== nextProps.shape.text ||
      prevProps.shape.textOffsetX !== nextProps.shape.textOffsetX ||
      prevProps.shape.textOffsetY !== nextProps.shape.textOffsetY ||
      prevProps.shape.groupingKey !== nextProps.shape.groupingKey ||
      prevProps.groupingKey !== nextProps.groupingKey ||
      prevProps.displayUnitSystem !== nextProps.displayUnitSystem ||
      prevProps.showOverlay !== nextProps.showOverlay ||
      prevProps.allScales !== nextProps.allScales
    ) return false;

    // Deep comparison helper for points arrays to ensure all intermediate vertices update
    const pPts = prevProps.shape.points;
    const nPts = nextProps.shape.points;
    if (!arePointsEqual(pPts, nPts)) return false;

    const pHoles = prevProps.shape.holes;
    const nHoles = nextProps.shape.holes;
    if ((pHoles?.length ?? 0) !== (nHoles?.length ?? 0)) return false;

    const pTids = prevProps.shape.trackedShapeIds;
    const nTids = nextProps.shape.trackedShapeIds;
    if ((pTids?.length ?? 0) !== (nTids?.length ?? 0)) return false;

    const pDk = prevProps.displayKeys;
    const nDk = nextProps.displayKeys;
    if ((pDk?.length ?? 0) !== (nDk?.length ?? 0)) return false;
    if (pDk?.length && (pDk[0] !== nDk[0] || pDk[pDk.length - 1] !== nDk[nDk.length - 1])) return false;

    // selectedShapeIds: only length matters for dimming logic
    if (prevProps.selectedShapeIds?.length !== nextProps.selectedShapeIds?.length) return false;

    return true;
  },
);

export default ShapeRenderer;
